import React from 'react';

import {
    emoji,
    expression,
    INLINE_BUTTON,
    INLINE_EXPRESSION,
    INLINE_ICON,
    INLINE_MATH,
    icon,
    images,
    math,
} from '@gitbook/doc-core';
import { useHiveClient } from '@gitbook/hive-react';
import {
    createPickEmojiPalette,
    type PaletteAnchorRef,
    type PaletteCommandDef,
    usePaletteHandle,
} from '@gitbook/react-uikit';
import { isBlockRangeSelection, type TextRange } from '@gitbook/slate';
import type { EditorContainer } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import type { IconDefinition } from '@gitbook/spine-react';

import { useContentReferenceContext } from '../ContentReferences';
import { createPickIconPalette } from '../Icons';
import { useInsertFiles } from '../InsertFilesDialog';
import { ContentEditorContext } from './contexts';
import { toggleButton, toggleLink } from './hovering-menu';
import type { InlineControl } from './inline-controls';

export type InlineCommandsHookProps = {
    editor: EditorContainer;
    range?: TextRange;
    anchorRef: PaletteAnchorRef;
};

type InlineCommand = {
    key: InlineControl;
    title: string;
    upgrade?: boolean;
    badge?: PaletteCommandDef['badge'];
    icon: IconDefinition;
    trigger: () => void;
};

export function useInlineCommands(props: InlineCommandsHookProps): InlineCommand[] {
    const { anchorRef, editor, range } = props;
    const { logAnalyticsEvent } = React.useContext(ContentEditorContext);
    const hiveClient = useHiveClient();
    const {
        blocks: { withImages },
    } = React.useContext(ContentEditorContext);
    const contentReferenceContext = useContentReferenceContext();
    const insertFiles = useInsertFiles();
    const isInsertingLinkRef = React.useRef(false);

    const paletteHandle = usePaletteHandle(anchorRef, {
        onClose: () => {
            paletteHandle.close();
            isInsertingLinkRef.current = false;
            if (insertFiles.isOpen()) {
                return;
            }
            editor.change((change) => change.focus());
        },
    });

    return React.useMemo<InlineCommand[]>(() => {
        const commands: InlineCommand[] = [];

        if (withImages) {
            commands.push({
                key: 'inline-image',
                title: 'Inline image',
                icon: Icon.Image,
                trigger: () => {
                    // to make sure focus is not on the editor otherwise the palette will stay open
                    editor.change((change) => change.blur());
                    insertFiles
                        .prompt({
                            title: 'Select image',
                            accept: 'images',
                            acceptURL: true,
                        })
                        .then((fileRefs) => {
                            editor.change((change) => {
                                if (isBlockRangeSelection(change.value.selection)) {
                                    return change;
                                }
                                const textRange = range ?? change.value.textSelection;
                                fileRefs.forEach((ref) => {
                                    change.insertInlineAtRange(
                                        textRange,
                                        images.createImageInline(ref)
                                    );
                                });
                                change.focus();
                            });
                        });
                },
            });
        }

        commands.push({
            key: 'button',
            title: 'Button',
            icon: Icon.Button,
            trigger: () => {
                isInsertingLinkRef.current = true;

                logAnalyticsEvent({
                    type: 'edit_page_document_insert_inline',
                    from: 'inline-palette',
                    inlineType: INLINE_BUTTON,
                });
                return toggleButton({
                    hiveClient,
                    palette: paletteHandle,
                    editor,
                    contentReferenceContext,
                    range,
                })();
            },
        });

        commands.push(
            {
                key: 'emoji',
                title: 'Emoji',
                icon: Icon.EmojiReaction,
                trigger: () => {
                    return createPickEmojiPalette((code) => {
                        editor.change((change) => {
                            if (isBlockRangeSelection(change.value.selection)) {
                                return change;
                            }
                            const textRange = range ?? change.value.textSelection;
                            change.insertInlineAtRange(textRange, emoji.createEmoji(code)).focus();
                        });
                    });
                },
            },
            {
                key: 'link',
                title: 'Link',
                icon: Icon.Link,
                trigger: () => {
                    isInsertingLinkRef.current = true;

                    return toggleLink({
                        hiveClient,
                        palette: paletteHandle,
                        editor,
                        contentReferenceContext,
                        isInLink: false,
                        insertAtRange: range,
                    })();
                },
            },
            {
                key: 'inline-math',
                title: 'Math & TeX',
                icon: Icon.MathTeX,
                trigger: () => {
                    logAnalyticsEvent({
                        type: 'edit_page_document_insert_inline',
                        from: 'inline-palette',
                        inlineType: INLINE_MATH,
                    });

                    editor.change((change) => {
                        if (isBlockRangeSelection(change.value.selection)) {
                            return change;
                        }
                        const textRange = range ?? change.value.textSelection;
                        change.insertInlineAtRange(textRange, math.createMathInline()).focus();
                    });
                },
            },
            {
                key: INLINE_ICON,
                title: 'Icon',
                icon: Icon.EmojiFlag,
                trigger: () => {
                    logAnalyticsEvent({
                        type: 'edit_page_document_insert_inline',
                        from: 'inline-palette',
                        inlineType: INLINE_ICON,
                    });

                    return createPickIconPalette((iconName) => {
                        editor.change((change) => {
                            if (isBlockRangeSelection(change.value.selection)) {
                                return change;
                            }
                            const textRange = range ?? change.value.textSelection;
                            change
                                .insertInlineAtRange(textRange, icon.createIcon(iconName))
                                .focus();
                        });
                    });
                },
            },
            {
                key: INLINE_EXPRESSION,
                title: 'Expression',
                icon: Icon.Expression,
                trigger: () => {
                    logAnalyticsEvent({
                        type: 'edit_page_document_insert_inline',
                        from: 'inline-palette',
                        inlineType: INLINE_EXPRESSION,
                    });

                    editor.change((change) => {
                        if (isBlockRangeSelection(change.value.selection)) {
                            return change;
                        }
                        const textRange = range ?? change.value.textSelection;
                        const inline = expression.createExpression();
                        change
                            .insertInlineAtRange(textRange, inline)
                            .collapseToStartOf(inline)
                            .focus();
                    });
                },
            }
        );

        return commands;
    }, [
        withImages,
        insertFiles,
        hiveClient,
        paletteHandle,
        editor,
        contentReferenceContext,
        range,
        logAnalyticsEvent,
    ]);
}
